// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Importing several RTAB-Map sessions into one case (frontend-aev.1, over
 * ruxd PR #19's `/imports/rtabmap`): the file check, the per-session queue
 * states, progress / ETA and the Danish error lines. Pure and DOM-free; the
 * chunk sender itself is `sendIfcChunks` (same protocol as the IFC upload).
 *
 * Each session is appended DETACHED where its own map put it, gets its own
 * scan id and a pose version `import`; the server refuses the same bytes
 * twice (409 `session_already_imported`).
 */

import { ApiRequestError } from '../api/client';
import type { Job } from '../api/types';
import { formatBytes } from '../app/cases';

/** The first 16 bytes of every SQLite database (an RTAB-Map `.db` is one). */
export const SQLITE_MAGIC = 'SQLite format 3\u0000';
export const SQLITE_HEAD_BYTES = 16;
/** ruxd's per-file cap (4 GiB); the upload's own `max_bytes` wins once known. */
export const RTABMAP_MAX_BYTES = 4 * 1024 ** 3;

export function hasSqliteHeader(head: Uint8Array): boolean {
  if (head.length < SQLITE_HEAD_BYTES) return false;
  for (let i = 0; i < SQLITE_HEAD_BYTES; i += 1) if (head[i] !== SQLITE_MAGIC.charCodeAt(i)) return false;
  return true;
}

/** Why a picked file cannot be a session, or null when it can. */
export function rtabmapFileProblem(name: string, size: number, head: Uint8Array | null, maxBytes = RTABMAP_MAX_BYTES): string | null {
  if (!name.toLowerCase().endsWith('.db')) return 'Ikke en RTAB-Map-database (.db).';
  if (size === 0) return 'Filen er tom.';
  if (size > maxBytes) return `Filen fylder ${formatBytes(size)} — serveren tager højst ${formatBytes(maxBytes)}.`;
  if (head && !hasSqliteHeader(head)) return 'Filen er ikke en SQLite-database, så den kan ikke være en RTAB-Map-session.';
  return null;
}

/** One session's way through the dialog. */
export type SessionState =
  | { kind: 'queued' }
  | { kind: 'invalid'; reason: string }
  | { kind: 'uploading'; sent: number; total: number; startedAt: number; notice: string | null }
  | { kind: 'starting' }
  | { kind: 'importing'; jobId: string }
  | { kind: 'done'; summary: string }
  | { kind: 'duplicate'; message: string }
  | { kind: 'failed'; message: string }
  | { kind: 'cancelled' };

export interface SessionItem {
  /** Stable key for React (name + size + index). */
  key: string;
  name: string;
  size: number;
  state: SessionState;
}

export function isFinished(state: SessionState): boolean {
  return ['invalid', 'done', 'duplicate', 'failed', 'cancelled'].includes(state.kind);
}

export function isBusy(state: SessionState): boolean {
  return state.kind === 'uploading' || state.kind === 'starting' || state.kind === 'importing';
}

/** Seconds left at the rate so far, or null before there is a rate worth trusting. */
export function etaSeconds(sent: number, total: number, elapsedMs: number): number | null {
  if (sent <= 0 || elapsedMs < 500 || sent >= total) return null;
  const rate = sent / elapsedMs;
  return Math.max(0, (total - sent) / rate / 1000);
}

export function formatEta(seconds: number | null): string {
  if (seconds === null) return '';
  if (seconds < 60) return `ca. ${Math.max(1, Math.round(seconds))} s tilbage`;
  const m = Math.round(seconds / 60);
  if (m < 60) return `ca. ${m} min tilbage`;
  return `ca. ${Math.floor(m / 60)} t ${m % 60} min tilbage`;
}

/** 0..1 for the session's own bar: upload is the first 90 %, the import job the rest. */
export function sessionFraction(state: SessionState, job?: Job | null): number {
  switch (state.kind) {
    case 'uploading':
      return state.total > 0 ? (0.9 * state.sent) / state.total : 0;
    case 'starting':
      return 0.9;
    case 'importing':
      return 0.9 + 0.1 * Math.min(1, Math.max(0, job?.progress?.fraction ?? 0));
    case 'done':
      return 1;
    default:
      return 0;
  }
}

/** The whole import's bar, weighted by file size; finished-but-failed files count as done. */
export function overallFraction(items: readonly SessionItem[], jobs: Readonly<Record<string, Job>> = {}): number {
  let total = 0;
  let done = 0;
  for (const it of items) {
    if (it.state.kind === 'invalid') continue;
    total += it.size;
    const job = it.state.kind === 'importing' ? jobs[it.state.jobId] : null;
    done += it.size * (isFinished(it.state) ? 1 : sessionFraction(it.state, job));
  }
  return total > 0 ? done / total : 0;
}

/** What an import job's summary says ("session (scan) 2, 1809 frames (nodes 1742..3589)"), as Danish. */
export function importJobSummary(job: Job): string {
  const s = job.result?.summary ?? '';
  const m = /session \(scan\) (\d+), (\d+) frames \(nodes (\d+)\.\.(\d+)\)/.exec(s);
  if (!m) return s || 'Importeret.';
  const frames = Number(m[2]).toLocaleString('da-DK');
  return `Session ${m[1]}: ${frames} billeder (knude ${m[3]}–${m[4]}), frakoblet`;
}

export type RtabmapStep = 'begin' | 'chunk' | 'complete';

/** The Danish line for a refused upload or import start. */
export function rtabmapErrorText(cause: unknown, step: RtabmapStep): string {
  if (cause instanceof RangeError) return cause.message;
  if (!(cause instanceof ApiRequestError)) return 'Kunne ikke nå serveren. Tjek forbindelsen, og prøv igen.';
  const { code } = cause;
  switch (cause.status) {
    case 401:
      return 'Du er ikke logget ind længere. Log ind igen, og prøv så igen.';
    case 403:
      return 'Du har kun læseadgang til sagen. Kun redaktører og ejere kan importere.';
    case 404:
      return 'Uploaden findes ikke længere (afbrudt eller udløbet). Vælg filen igen.';
    case 409:
      if (code === 'session_already_imported') return 'Sessionen er allerede importeret i sagen — den springes over.';
      if (code === 'rtabmap_upload_completing') return 'Uploaden er ved at blive afsluttet i forvejen.';
      if (code === 'upload_incomplete' || step === 'complete') return 'Ikke hele filen nåede frem. Prøv igen.';
      if (code === 'case_busy') return 'Et job kører på sagen. Vent, til det er færdigt.';
      return 'Uploaden kom ud af trit med serveren. Prøv igen.';
    case 413:
      return code === 'rtabmap_quota_exceeded'
        ? 'Sagens plads til ventende sessioner er brugt op (16 GB). Vent, til en import er færdig.'
        : 'Filen er for stor (højst 4 GB pr. session).';
    case 422:
      return 'Filen er ikke en SQLite-database, så serveren har slettet den.';
    case 429:
      return 'Der er for mange session-uploads i gang. Vent, til en af dem er færdig.';
    case 507:
      return 'Serveren har ikke plads til filen lige nu.';
  }
  return step === 'complete' ? 'Importen kunne ikke startes.' : 'Uploaden fejlede.';
}

/** The import job's failure, in Danish with ruxd's own reason after it. */
export function rtabmapJobErrorText(job: Job): string {
  if (job.status === 'cancelled') return 'Importen blev annulleret.';
  const reason = job.error?.trim();
  return reason ? `Importen fejlede: ${reason}` : 'Importen fejlede.';
}
